// src/atoms/inputs/FormField.tsx

import {InputGroup, Label} from "@blueprintjs/core";
import styled from "@emotion/styled";
import React from "react";

// Blueprint の InputGroup が受け取るすべての props を拾う型
type InputProps = React.ComponentProps<typeof InputGroup>;

/**
 * FormField の props
 * - label      : ラベルに表示するテキスト（省略可）
 * - required   : 必須マークをつけるかどうか（省略時は false）
 * - errorText  : エラーメッセージ（空文字または undefined なら何も表示しない）
 * - noLabel    : true にするとラベル領域を出さず、入力＋エラーだけ表示
 * - ...inputProps : Blueprint InputGroup に渡したいすべての props（placeholder/value/onChange など）
 */
export type FormFieldProps = {
    label?: string;
    required?: boolean;
    errorText?: string;
    noLabel?: boolean;
} & InputProps;

/**
 * Wrapper：
 * - ラベルと「入力欄＋エラー」を横並びにする
 * - noLabel のときはラベル領域がなくなるぶん、
 *   align-items: center で入力＋エラーを上下中央に揃える
 */
const Wrapper = styled.div<{noLabel?: boolean}>`
    display: flex;
    align-items: ${({noLabel}) => (noLabel ? "center" : "flex-start")};
    gap: 16px;

    > label {
        margin-bottom: 0px; /* Blueprint Label のデフォルト margin-bottom をリセット */
        white-space: nowrap;
        margin-top: 6px;
    }
`;

/** 入力欄とエラーメッセージを縦方向に積む */
const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px; /* 入力とエラーの間隔 */
    width: 100%;
`;

/** 必須マーク付き Label */
const CustomLabel = styled(Label)<{required?: boolean}>(({required = false}) => ({
    fontWeight: 500,
    ...(required && {
        "&::after": {
            content: '"*"',
            color: "red",
            marginLeft: "0.2em",
        },
    }),
}));

/** disabled 時に背景を透明化する InputGroup のラップ */
const CustomInput = styled(InputGroup)<InputProps>(({disabled}) => ({
    ...(disabled && {
        "& .bp3-input:disabled": {
            backgroundColor: "transparent",
            color: "inherit",
            opacity: 1,
            padding: 0,
        },
    }),
}));

/** エラーメッセージを赤字・小さめフォントで表示 */
export const ErrorText = styled.span`
    display: block;
    color: red;
    font-size: 12px;
    line-height: 1.4;
    white-space: pre-line; /* 改行を保持 */
`;

export const FormField: React.FC<FormFieldProps> = ({
    label,
    required = false,
    errorText,
    noLabel = false,
    ...inputProps
}) => {
    return (
        <Wrapper noLabel={noLabel}>
            {/* ラベルを表示しない場合は省略 */}
            {!noLabel && label && (
                <CustomLabel htmlFor={label} required={required}>
                    {label}
                </CustomLabel>
            )}

            <InputWrapper>
                <CustomInput id={label} {...inputProps} />
                {errorText && <ErrorText>{errorText}</ErrorText>}
            </InputWrapper>
        </Wrapper>
    );
};
